import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  TextField,
  Typography
} from "@material-ui/core";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from "@material-ui/icons";
import { DebounceInput } from "react-debounce-input";
import { useTranslation } from "react-i18next";
import { filterPermissionTree } from "./buildPermissionTree";
import {
  FolderBlock,
  FolderLabel,
  LeafLabel,
  LeafRow,
  NodeRow,
  TreePanel,
  TreeWrap
} from "../../../components/CheckboxTree/CheckboxTreeStyles";

class TreeNode extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: true };
  }

  toggle = () => {
    this.setState(prev => ({ open: !prev.open }));
  };

  render() {
    const {
      node,
      depth,
      selectedIds,
      onTogglePermission,
      forceOpen
    } = this.props;
    const { open: openState } = this.state;
    const open = forceOpen || openState;
    const hasChildren =
      (node.children && node.children.length > 0) ||
      (node.permissions && node.permissions.length > 0);

    return (
      <FolderBlock style={{ marginLeft: depth > 0 ? 12 : 0 }}>
        <NodeRow
          $clickable={hasChildren}
          onClick={hasChildren ? this.toggle : undefined}
          role={hasChildren ? "button" : undefined}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                this.toggle();
              }}
            >
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : (
            <Box width={30} />
          )}
          <FolderLabel>{node.label}</FolderLabel>
        </NodeRow>
        <Collapse in={open}>
          {(node.permissions || []).map(permission => {
            const checked = selectedIds.has(permission.id);
            return (
              <LeafRow key={permission.id} $checked={checked}>
                <FormControlLabel
                  style={{ margin: 0, width: "100%" }}
                  control={(
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={checked}
                      onChange={() => onTogglePermission(permission.id)}
                    />
                  )}
                  label={(
                    <LeafLabel>
                      {permission.description ||
                        `${permission.action} · ${permission.subject}`}
                    </LeafLabel>
                  )}
                />
              </LeafRow>
            );
          })}
          {(node.children || []).map(child => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              onTogglePermission={onTogglePermission}
              forceOpen={forceOpen}
            />
          ))}
        </Collapse>
      </FolderBlock>
    );
  }
}

TreeNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    permissions: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  depth: PropTypes.number.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onTogglePermission: PropTypes.func.isRequired,
  forceOpen: PropTypes.bool
};

TreeNode.defaultProps = {
  forceOpen: false
};

const PermissionTree = ({ tree, selectedIds, onTogglePermission }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const filteredTree = useMemo(() => filterPermissionTree(tree, searchText), [
    tree,
    searchText
  ]);
  const searching = searchText.trim().length > 0;

  return (
    <TreeWrap>
      <DebounceInput
        element={TextField}
        debounceTimeout={250}
        label={t("settings.permissions.search")}
        placeholder={t("settings.permissions.placeholder")}
        variant="outlined"
        size="small"
        fullWidth
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
      />
      <TreePanel>
        {filteredTree.length === 0 && (
          <Typography color="textSecondary">
            {searching
              ? t("settings.permissions.emptySearch")
              : t("settings.permissions.emptyCampus")}
          </Typography>
        )}
        {filteredTree.map(node => (
          <TreeNode
            key={node.id}
            node={node}
            depth={0}
            selectedIds={selectedIds}
            onTogglePermission={onTogglePermission}
            forceOpen={searching}
          />
        ))}
      </TreePanel>
    </TreeWrap>
  );
};

PermissionTree.propTypes = {
  tree: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onTogglePermission: PropTypes.func.isRequired
};

export default PermissionTree;
